import { useCallback, useEffect, useRef, useState } from "react";

const CHAT_NAV_KEY = "chatNav";

type ChatNavLayer = "list" | "thread" | "info" | "lightbox";

type ChatHistoryState = {
  [CHAT_NAV_KEY]?: ChatNavLayer;
};

function layerOf(opts: {
  mobilePane: "list" | "thread";
  showInfo: boolean;
  lightbox: boolean;
}): ChatNavLayer {
  if (opts.lightbox) return "lightbox";
  if (opts.mobilePane === "thread" && opts.showInfo) return "info";
  if (opts.mobilePane === "thread") return "thread";
  return "list";
}

function depth(layer: ChatNavLayer): number {
  switch (layer) {
    case "lightbox":
      return 3;
    case "info":
      return 2;
    case "thread":
      return 1;
    default:
      return 0;
  }
}

function useIsNarrowMobile() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return mobile;
}

/**
 * Geste / bouton retour mobile : lightbox → infos → fil → liste,
 * avant de quitter le Chat vers la Suite.
 */
export function useChatMobileHistory({
  mobilePane,
  setMobilePane,
  showInfo,
  setShowInfo,
  lightboxOpen,
  closeLightbox,
  ctxMenuOpen,
  closeCtxMenu,
}: {
  mobilePane: "list" | "thread";
  setMobilePane: (pane: "list" | "thread") => void;
  showInfo: boolean;
  setShowInfo: (v: boolean) => void;
  lightboxOpen: boolean;
  closeLightbox: () => void;
  ctxMenuOpen: boolean;
  closeCtxMenu: () => void;
}) {
  const enabled = useIsNarrowMobile();
  const lastLayerRef = useRef<ChatNavLayer | null>(null);
  const popstateRef = useRef(false);
  const bootstrappedRef = useRef(false);
  const ctxRef = useRef({
    mobilePane,
    showInfo,
    lightboxOpen,
    ctxMenuOpen,
    setMobilePane,
    setShowInfo,
    closeLightbox,
    closeCtxMenu,
  });

  ctxRef.current = {
    mobilePane,
    showInfo,
    lightboxOpen,
    ctxMenuOpen,
    setMobilePane,
    setShowInfo,
    closeLightbox,
    closeCtxMenu,
  };

  const applyLayer = useCallback((layer: ChatNavLayer) => {
    const ctx = ctxRef.current;
    if (layer === "list") {
      ctx.closeLightbox();
      ctx.closeCtxMenu();
      ctx.setShowInfo(false);
      ctx.setMobilePane("list");
      return;
    }
    if (layer === "thread") {
      ctx.closeLightbox();
      ctx.closeCtxMenu();
      ctx.setShowInfo(false);
      ctx.setMobilePane("thread");
      return;
    }
    if (layer === "info") {
      ctx.closeLightbox();
      ctx.closeCtxMenu();
      ctx.setMobilePane("thread");
      ctx.setShowInfo(true);
      return;
    }
    ctx.closeLightbox();
  }, []);

  /** Retour UI (flèche) : consomme l’entrée historique si présente. */
  const goBack = useCallback(() => {
    if (!enabled) {
      applyLayer("list");
      return;
    }
    const state = window.history.state as ChatHistoryState | null;
    if (state?.[CHAT_NAV_KEY] && state[CHAT_NAV_KEY] !== "list") {
      window.history.back();
      return;
    }
    applyLayer("list");
  }, [applyLayer, enabled]);

  /** Ferme le calque courant via l’historique (infos / lightbox). */
  const dismissOverlay = useCallback(() => {
    if (!enabled) return false;
    const state = window.history.state as ChatHistoryState | null;
    const layer = state?.[CHAT_NAV_KEY];
    if (layer === "info" || layer === "lightbox") {
      window.history.back();
      return true;
    }
    return false;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const onPopState = (e: PopStateEvent) => {
      popstateRef.current = true;
      const ctx = ctxRef.current;

      if (ctx.ctxMenuOpen) {
        ctx.closeCtxMenu();
        const keep = lastLayerRef.current ?? "thread";
        const snap: ChatHistoryState = { [CHAT_NAV_KEY]: keep };
        window.history.pushState({ ...(window.history.state as object), ...snap }, "");
        queueMicrotask(() => {
          popstateRef.current = false;
        });
        return;
      }

      const next = ((e.state as ChatHistoryState | null)?.[CHAT_NAV_KEY] ?? "list") as ChatNavLayer;
      // Ne jamais restaurer un fil « fantôme » hors app — toujours une couche chat connue
      applyLayer(next === "lightbox" || next === "info" || next === "thread" || next === "list" ? next : "list");
      lastLayerRef.current = next;

      queueMicrotask(() => {
        popstateRef.current = false;
      });
    };

    // Au boot mobile : forcer la liste (évite BFCache / state thread résiduel)
    if (!bootstrappedRef.current) {
      bootstrappedRef.current = true;
      const initial: ChatHistoryState = { [CHAT_NAV_KEY]: "list" };
      window.history.replaceState({ ...(window.history.state as object), ...initial }, "");
      lastLayerRef.current = "list";
      applyLayer("list");
    }

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [applyLayer, enabled]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    if (popstateRef.current) return;
    if (!bootstrappedRef.current) return;

    const next = layerOf({
      mobilePane,
      showInfo,
      lightbox: lightboxOpen,
    });
    const prev = lastLayerRef.current;

    if (prev == null) {
      const snap: ChatHistoryState = { [CHAT_NAV_KEY]: next };
      window.history.replaceState({ ...(window.history.state as object), ...snap }, "");
      lastLayerRef.current = next;
      return;
    }
    if (prev === next) return;

    const snap: ChatHistoryState = { [CHAT_NAV_KEY]: next };
    if (depth(next) > depth(prev)) {
      window.history.pushState({ ...(window.history.state as object), ...snap }, "");
    } else {
      window.history.replaceState({ ...(window.history.state as object), ...snap }, "");
    }
    lastLayerRef.current = next;
  }, [enabled, mobilePane, showInfo, lightboxOpen]);

  return { goBack, dismissOverlay, mobileHistoryEnabled: enabled };
}
